import { SEARCH_DEBOUNCE_MS } from "@khazzin/ui/patterns/page";
import { Input, type InputProps } from "@khazzin/ui/primitives";
import { useEffect, useRef, useState } from "react";

/**
 * A search box that holds its own text and hands it on after a pause
 * (ADJ-M03-4): a request per keystroke on "corrugated mailer" is eighteen
 * requests, and the list flickers through every one of them.
 *
 * A `value` that changes from outside — Clear, the back button — is adopted;
 * the text being typed is never overwritten by its own commit.
 */
export function SearchInput({
	value,
	onCommit,
	...rest
}: { value: string; onCommit: (q: string) => void } & Omit<InputProps, "value" | "onChange">) {
	const [text, setText] = useState(value);
	const commit = useRef(onCommit);
	commit.current = onCommit;

	useEffect(() => {
		setText((current) => (current === value ? current : value));
	}, [value]);

	useEffect(() => {
		if (text === value) {
			return;
		}
		const timer = window.setTimeout(() => commit.current(text), SEARCH_DEBOUNCE_MS);
		return () => window.clearTimeout(timer);
	}, [text, value]);

	return <Input type="search" value={text} onChange={(e) => setText(e.target.value)} {...rest} />;
}
